import { useCallback, useMemo, useRef } from "react";
import pusher from "../utils/pusher";

/**
 * Eventos de Pusher a los que escucha el dashboard.
 *
 * Vive fuera del hook a propósito: como array dentro del cuerpo se recreaba en
 * cada render y arrastraba a las funciones que lo usan a cambiar de identidad.
 */
const SERVICES = [
  "onLinkedChannel",
  "newMessage",
  "newClient",
  "updateUserData",
  "deleteConversation",
  "archived",
  "archivedBroadcastConversation",
  "markAsRead",
  "markAsUnread",
  "conversationValuable",
  "messageStatus",
  "finishBotAssignment",
  "newUserName",
  "tagsUpdate",
  "unassignClient",
  "openClient",
  "groupAdmin",
];

const isUserAdmin = (rolName) =>
  rolName === "admin" || rolName === "superadmin" || rolName === "groupadmin";

/**
 * Suscribe el dashboard al canal de Pusher que le corresponde al usuario y
 * despacha cada evento entrante a `handleNotification`.
 *
 * ## Por qué este hook se reescribió (DASHBOARD-ZC-KK / -KV / -KT)
 *
 * La versión anterior devolvía `{ subscribe, unsubscribe }` como objeto literal
 * y recreaba `unsubscribe` en cada render. Ese objeto está en el array de
 * dependencias del efecto de `DashboardLayout` que llama a `subscribe()`, así
 * que **el efecto se rehacía en cada render del layout**: `pusher.unsubscribe()`
 * + `pusher.subscribe()` + re-bind de los 17 servicios, una y otra vez.
 *
 * Y no hacía falta ni navegar: `subscribe()` llamaba a `setChannel()`, lo que
 * provocaba un render, que cambiaba la identidad del binder, que rehacía el
 * efecto. Un solo montaje ya mandaba DOS suscripciones. A partir de ahí,
 * cualquier cambio de Redux que el layout observa (usuario, canales,
 * plantillas…) — o sea, constantemente en un chat con tráfico — repetía el
 * ciclo.
 *
 * Consecuencia para el asesor: durante cada ventana de churn el canal está
 * desatado y **los eventos que llegan en ese momento se pierden**. Es el
 * "no me llegan los mensajes en vivo, sólo al recargar" que reportaron
 * SCPodólogos, VINTE y SieteTrece. Y para Pusher: un goteo constante de
 * frames de suscripción/desuscripción por cada pestaña abierta.
 *
 * El arreglo es de identidad, no de lógica: el canal vive en un `ref` (no en
 * `useState`, que forzaba el render), `unsubscribe` se memoiza, y el objeto
 * devuelto es estable mientras no cambie el usuario. `subscribe()` además es
 * idempotente: llamarlo dos veces no abre una segunda suscripción.
 *
 * El `handleNotification` se lee desde un ref, así que un callback nuevo NO
 * obliga a resuscribirse — antes tampoco lo hacía (no estaba en las deps de
 * `useCallback`), pero por accidente: los handlers se quedaban con la versión
 * del primer bind. Ahora siempre se usa la última, sin tocar la suscripción.
 */
const useBinder = (user, handleNotification) => {
  const channelRef = useRef(null);
  const notifyRef = useRef(handleNotification);
  notifyRef.current = handleNotification;

  const companyId = user.data?.company?.id;
  const userId = user.data?.id;
  const rolName = user.data?.rol?.name;

  const channelName = isUserAdmin(rolName)
    ? `company_${companyId}`
    : `agent_${userId}`;

  const unsubscribe = useCallback(() => {
    const channel = channelRef.current;
    if (!channel) return;

    console.log("Binder:: Unsubscribe to channel: ", channel.name);
    SERVICES.forEach((service) => channel.unbind(service));
    // Se suelta el canal que REALMENTE se suscribió, no el que tocaría según
    // el usuario de ahora: si el usuario cambió, el nombre ya no coincide y
    // la versión anterior dejaba el canal viejo colgado.
    pusher.unsubscribe(channel.name);
    channelRef.current = null;
  }, []);

  const subscribe = useCallback(() => {
    if (channelRef.current) return channelRef.current;
    if (!companyId) return null;

    const channel = pusher.subscribe(channelName);
    channelRef.current = channel;

    console.log("Binder:: Subscribing to channel: ", channel.name);
    SERVICES.forEach((service) => {
      channel.bind(service, (data) => notifyRef.current(data));
    });

    return channel;
  }, [companyId, channelName]);

  return useMemo(() => ({ subscribe, unsubscribe }), [subscribe, unsubscribe]);
};

export default useBinder;
